import React from 'react';
import { Gift, Users, Heart, CheckCircle2 } from 'lucide-react';
import Button from 'shared/components/ui/Button';
import { Card } from 'shared/components/ui/Card';
import { useTranslation } from 'react-i18next';
import { URLS } from 'configs';
import './SharedWishlistSection.scss';

const SharedWishlistSection = () => {
  const { t } = useTranslation();

  const steps = [
    {
      icon: Heart,
      title: t('wishlist_step_1_title', 'Create Your Wishlist'),
      description: t(
        'wishlist_step_1_desc',
        'Organize items for birthdays, weddings, holidays, or any special occasion. Add products from any store.',
      ),
    },
    {
      icon: Users,
      title: t('wishlist_step_2_title', 'Share with Loved Ones'),
      description: t(
        'wishlist_step_2_desc',
        "Invite friends and family to view your wishlist. They'll see exactly what you want, ensuring perfect gifts.",
      ),
    },
    {
      icon: Gift,
      title: t('wishlist_step_3_title', 'Browse Their Lists'),
      description: t(
        'wishlist_step_3_desc',
        "Navigate through friends' wishlists to select the perfect present. Make gift-giving stress-free and meaningful.",
      ),
    },
  ];

  const occasions = [
    t('wishlist_occasion_1', 'Birthday wishlists'),
    t('wishlist_occasion_2', 'Wedding registries'),
    t('wishlist_occasion_3', 'Holiday gift guides'),
    t('wishlist_occasion_4', 'Baby showers'),
    t('wishlist_occasion_5', 'Housewarming parties'),
    t('wishlist_occasion_6', 'Graduation celebrations'),
  ];

  return (
    <section className='wishlist-section'>
      <div className='wishlist-section__container'>
        <div className='wishlist-section__header'>
          <div className='wishlist-section__badge'>
            <Gift className='wishlist-section__badge-icon' aria-hidden='true' />
            <span className='wishlist-section__badge-text'>{t('wishlist_badge', 'Smart Wishlists')}</span>
          </div>

          <h2 className='wishlist-section__title'>
            {t('wishlist_title_1', "Gifts They'll Love,")}{' '}
            <span className='wishlist-section__title--gradient'>{t('wishlist_title_2', 'Shared & Simplified')}</span>
          </h2>

          <p className='wishlist-section__description'>
            {t(
              'wishlist_description',
              'Create wishlists that friends and family can access and contribute to. Make celebrations personal and gifts meaningful.',
            )}
          </p>
        </div>

        <div className='wishlist-section__steps'>
          {steps.map((step, i) => (
            <Card key={i} className='wishlist-section__step-card'>
              <div className='wishlist-section__step-icon-wrapper'>
                <step.icon className='wishlist-section__step-icon' />
              </div>
              <h3 className='wishlist-section__step-title'>{step.title}</h3>
              <p className='wishlist-section__step-description'>{step.description}</p>
            </Card>
          ))}
        </div>

        <Card className='wishlist-section__occasions-card'>
          <div className='wishlist-section__occasions-grid'>
            <div className='wishlist-section__occasions-content'>
              <h3 className='wishlist-section__occasions-title'>
                {t('wishlist_occasions_title', 'Perfect for Every Occasion')}
              </h3>
              <div className='wishlist-section__occasions-list'>
                {occasions.map((occasion, i) => (
                  <div key={i} className='wishlist-section__occasion'>
                    <CheckCircle2 className='wishlist-section__occasion-icon' />
                    <span className='wishlist-section__occasion-text'>{occasion}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className='wishlist-section__occasions-visual'>
              <div className='wishlist-section__gift-icon-wrapper'>
                <Gift className='wishlist-section__gift-icon' />
              </div>
              <a href={URLS.CONTACT} style={{ width: '100%' }}>
                <Button variant='default' size='lg' style={{ width: '100%' }}>
                  {t('contact_us', 'Contact us')}
                </Button>
              </a>
            </div>
          </div>
        </Card>
      </div>

      {/* Decorative Elements */}
      <div className='wishlist-section__decoration' aria-hidden='true' />
    </section>
  );
};

export default SharedWishlistSection;
